"use strict";

var _ = require("lodash");

module.exports = /* @ngInject */ function(
  $q,
  $log,
  $location,
  openFinApi,
  openFinWebLibFeatures,
  taskbarIconUrl,
  openFinWebClientFeatures,
  openFinWindowUtils,
  rediVersionCheck
) {
  return {
    createSimpleOpenfinWindow: createSimpleOpenfinWindow,
    createOpenfinWindow: createOpenfinWindow,
    executeOnOpenWindows: executeOnOpenWindows,
    executeOnCloseWindows: executeOnCloseWindows,
    getOpenWindowCount: getOpenWindowCount,
    clearCache: clearCache,
    executeCloseAllWindows: executeCloseAllWindows,
    createInvisibleOpenfinWindow: createInvisibleOpenfinWindow,
    createAndShowErrorWindow: createAndShowErrorWindow
  };

  function getOpenWindowCount() {
    return openFinApi.getFin().then(function(fin) {
      var app = fin.desktop.Application.getCurrent();
      var windowCountDefered = $q.defer();

      app.getChildWindows(
        function(children) {
          windowCountDefered.resolve(children.length);
        },
        function() {
          windowCountDefered.reject(0);
        }
      );

      return windowCountDefered.promise;
    });
  }

  function executeCloseAllWindows() {
    return getOpenWindowCount().then(function(numberOfOpenWindows) {
      var closeCount = 0;
      return $q(function(resolve) {
        if (numberOfOpenWindows === 0) {
          resolve();
        } else {
          executeOnCloseWindows(function() {
            ++closeCount;
            if (numberOfOpenWindows === closeCount) {
              resolve();
            }
          });
        }
      });
    });
  }

  function applyActions(action, child, callback) {
    if (openFinWebLibFeatures.enableHideTaskbarIcon) {
      if (action === "minimize") {
        child.hide(function() {
          child.minimize(callback);
        });
        return;
      }
      if (action === "restore") {
        child.show(function() {
          child.restore(callback);
        });
        return;
      }
    }
    child[action](callback);
  }

  function executeOnOpenWindows(action, callback) {
    openFinApi.getFin().then(function(fin) {
      var app = fin.desktop.Application.getCurrent();
      app.getChildWindows(function(children) {
        children.forEach(function(child) {
          applyActions(action, child, callback);
        });
      });
    });
  }

  function executeOnCloseWindows(checkIfAllChildWindowsClosed) {
    openFinApi.getFin().then(function(fin) {
      var app = fin.desktop.Application.getCurrent();
      app.getChildWindows(function(children) {
        children.forEach(function(child) {
          child.getOptions(function(options) {
            if (options.hideOnClose || child.name === "openfinLauncher") {
              child.minimize(true);
              checkIfAllChildWindowsClosed();
            } else if (child.name === "Error Window") {
              checkIfAllChildWindowsClosed();
            } else {
              child.close(true, checkIfAllChildWindowsClosed);
            }
          });
        });
      });
    });
  }

  function clearCache() {
    return openFinApi.getFin().then(function(fin) {
      return $q(function(resolve, reject) {
        fin.desktop.System.clearCache(
          {
            cache: true,
            cookies: false,
            localStorage: false,
            appcache: true,
            userData: true
          },
          resolve,
          reject
        );
      });
    });
  }

  function createSimpleOpenfinWindow(url, name) {
    return openFinApi.getFin().then(function(fin) {
      var win = new fin.desktop.Window(
        {
          name: name,
          url: url,
          state: "normal"
        },
        function() {
          win.show();
        }
      );
    });
  }

  function createInvisibleOpenfinWindow(url, name) {
    return openFinWindowUtils.create({
      name: name,
      autoShow: false,
      url: url,
      showTaskbarIcon: false
    });
  }

  function createOpenfinWindow(rediWindow, initParams) {
    return openFinApi.getFin().then(function(fin) {
      var bus = fin.desktop.InterApplicationBus;
      var appUuid = fin.desktop.Application.getCurrent().uuid;
      var name = rediWindow.getName();
      var onWindowReady = function() {
        bus.send(appUuid, name, "init-window", initParams || {});
      };
      bus.subscribe(appUuid, name, "window-ready", onWindowReady);

      function setupBackgroundWindowHideAfterShown(openFinWindow) {
        openFinWindow.addEventListener("shown", function() {
          $log.warn(
            "The background window " +
              openFinWindow.name +
              " is shown. Re-hiding"
          );
          openFinWindow.hide();
        });
      }

      return createOptions(rediWindow, appUuid)
        .then(function(options) {
          return openFinWindowUtils
            .create(options)
            .then(function(windowInstance) {
              return workaroundOpenFinBugs(windowInstance, options);
            });
        })
        .then(
          function(openFinWindow) {
            openFinWindow.addEventListener("closed", function() {
              bus.unsubscribe(appUuid, name, "window-ready", onWindowReady);
            });

            if (rediWindow.isBackground()) {
              setupBackgroundWindowHideAfterShown(openFinWindow);
            }

            return openFinWindow;
          },
          function(err) {
            $log.error("Error creating window: " + err);
          }
        );
    });
  }

  function createOptions(rediWindow, appUuid) {
    var showTaskbarIcon =
      !openFinWebLibFeatures.enableHideTaskbarIcon &&
      !rediWindow.isBackground();

    var BACKGROUND_APP_WINDOW_SIZE = 0;
    var OS_DEFAULT_INDICATOR = -1;

    var minWindowSize = rediWindow.getMinWindowSize() || {
      width: 400,
      height: 500
    };

    var options = {
      name: rediWindow.getName(),
      minHeight: rediWindow.isBackground()
        ? BACKGROUND_APP_WINDOW_SIZE
        : minWindowSize.height,
      minWidth: rediWindow.isBackground()
        ? BACKGROUND_APP_WINDOW_SIZE
        : minWindowSize.width,
      maxHeight: rediWindow.isBackground()
        ? BACKGROUND_APP_WINDOW_SIZE
        : OS_DEFAULT_INDICATOR,
      maxWidth: rediWindow.isBackground()
        ? BACKGROUND_APP_WINDOW_SIZE
        : OS_DEFAULT_INDICATOR,
      autoShow: false,
      api: {
        iframe: {
          sameOriginInjection: true,
          crossOriginInjection: true
        }
      },
      resizable: true,
      frame: rediWindow.shouldShowFrame(),
      saveWindowState: false,
      url: rediWindow.getPath(),
      showTaskbarIcon: showTaskbarIcon,
      icon: getTaskbarIconUrl(rediWindow.isEikonWindow()),
      taskbarIconGroup: getTaskbarIconGroup(
        rediWindow.isEikonWindow(),
        appUuid
      ),
      contextMenu: false,
      hideOnClose: rediWindow.isAlwaysOpen()
    };

    options = useREDIFrame(options);

    return getFrameConnect(rediWindow).then(function(frameConnect) {
      options.frameConnect = frameConnect;
      return options;
    });
  }

  function getTaskbarIconUrl(isEikonWindow) {
    if (isEikonWindow) {
      return (
        $location.protocol() +
        "://" +
        $location.host() +
        ":" +
        $location.port() +
        "/openfin/images/eikon.ico"
      );
    } else {
      return taskbarIconUrl;
    }
  }

  function getTaskbarIconGroup(isEikonWindow, appUuid) {
    if (isEikonWindow) {
      return "EikonAppGroup";
    } else {
      return appUuid;
    }
  }

  function getFrameConnect(rediWindow) {
    var MINIMUM_FRAME_CONNECT_ALL_REDIPLUS_VERSION = "17.2.0.0";

    var ALL = "all";
    var MAIN_WINDOW = "main-window";

    return rediVersionCheck
      .isExpectingVersionFromRedi()
      .then(function(isExpecting) {
        return (
          isExpecting &&
          rediVersionCheck.isGreaterThanOrEqualTo(
            MINIMUM_FRAME_CONNECT_ALL_REDIPLUS_VERSION
          ) &&
          rediWindow.isREDIPlusWindow()
        );
      })
      .then(
        function(shouldConnectAll) {
          return shouldConnectAll ? ALL : MAIN_WINDOW;
        },
        function(error) {
          $log.warn(
            "Failed to properly determine frame connect. Using " + MAIN_WINDOW,
            error
          );
          return MAIN_WINDOW;
        }
      );
  }

  function useREDIFrame(options) {
    if (!openFinWebClientFeatures.useREDIFrame) {
      return options;
    }
    var newOptions = _.cloneDeep(options);
    if (options.frame) {
      newOptions.frame = false;
      newOptions.url =
        "/openfin/#/window?url=" + encodeURIComponent(options.url);
    }
    return newOptions;
  }

  function workaroundOpenFinBugs(windowInstance, options) {
    hookMinimizeOnClose(windowInstance, options);
    return windowInstance;
  }

  function hookMinimizeOnClose(windowInstance, options) {
    // To work around an OpenFin issue where you click the "X" button and "hideOnClose" is enabled, we want
    // to minimize the window when it is clicked so restoring the window will work.
    if (options.hideOnClose) {
      windowInstance.addEventListener("close-requested", function() {
        windowInstance.minimize();
      });
    }
  }

  function createAndShowErrorWindow(title, message) {
    return openFinWindowUtils.create({
      name: "Error Window",
      autoShow: true,
      url:
        "#/error?title=" +
        encodeURIComponent(title) +
        "&message=" +
        encodeURIComponent(message),
      resize: false,
      windowState: "normal",
      frame: false,
      maxWidth: 700,
      minWidth: 700,
      maxHeight: 250,
      minHeight: 250,
      contextMenu: false,
      maximizable: true,
      minimizable: false,
      icon: taskbarIconUrl
    });
  }
};



// WEBPACK FOOTER //
// ./app/infrastructure/factories/OpenfinWindowFactory.js